import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '../../context/ChatContext';
import { FiArrowLeft, FiMoreVertical, FiUsers, FiUserPlus, FiTrash2, FiCheck, FiRefreshCw, FiBellOff, FiBell } from 'react-icons/fi';
import { useToast } from '../commons/toast/ToastContext';
import ChatParticipantsPanel from './ChatParticipantsPanel';

const ChatHeader = ({ chatRoom, userId, isTrainer, schoolId, onBack }) => {
    const { t } = useTranslation('chat');
    const {
        finishTrainingGroupChat,
        renewTrainingGroupChat,
        deleteChat,
        addAllSchoolOwners,
        toggleMute,
        onlineUsers
    } = useContext(ChatContext);
    const toast = useToast();

    const [showMenu, setShowMenu] = useState(false);
    const [showParticipants, setShowParticipants] = useState(false);

    const currentParticipant = chatRoom.participants?.find(p => p.userId === userId);
    const isMuted = currentParticipant?.muted;

    // Online status for DIRECT chats
    const isDirectChat = chatRoom.type === 'DIRECT';
    const otherParticipant = isDirectChat
        ? chatRoom.participants?.find(p => p.userId !== userId && p.active !== false)
        : null;
    const isOtherOnline = otherParticipant ? onlineUsers[otherParticipant.userId] : false;

    const handleToggleMute = async () => {
        try {
            await toggleMute(chatRoom.id);
            toast.success(isMuted ? t('actions.unmuteSuccess') : t('actions.muteSuccess'));
        } catch (err) {
            toast.error(err.message);
        }
        setShowMenu(false);
    };

    const isAdmin = chatRoom.participants?.some(
        p => p.userId === userId && p.role === 'ADMIN'
    );

    const handleFinish = async () => {
        try {
            await finishTrainingGroupChat(chatRoom.id);
            toast.success(t('actions.finishSuccess'));
        } catch (err) {
            toast.error(err.message);
        }
        setShowMenu(false);
    };

    const handleRenew = async () => {
        try {
            await renewTrainingGroupChat(chatRoom.id);
            toast.success(t('actions.renewSuccess'));
        } catch (err) {
            toast.error(err.message);
        }
        setShowMenu(false);
    };

    const handleDelete = async () => {
        if (window.confirm(t('actions.deleteConfirm'))) {
            try {
                await deleteChat(chatRoom.id);
                toast.success(t('actions.deleteSuccess'));
            } catch (err) {
                toast.error(err.message);
            }
        }
        setShowMenu(false);
    };

    const handleAddAllOwners = async () => {
        try {
            await addAllSchoolOwners(chatRoom.id);
            toast.success(t('actions.addAllOwnersSuccess'));
        } catch (err) {
            toast.error(err.message);
        }
        setShowMenu(false);
    };

    const participantCount = chatRoom.participants?.filter(p => p.active !== false).length || 0;

    return (
        <>
            <div className="chat-header">
                <button className="chat-back-btn" onClick={onBack}>
                    <FiArrowLeft />
                </button>

                <div className="chat-header-info">
                    <h3 className="chat-header-name">
                        {isDirectChat && (
                            <span className={`online-dot ${isOtherOnline ? 'online' : 'offline'}`} />
                        )}
                        {chatRoom.name}
                    </h3>
                    <span className="chat-header-participants">
                        {isDirectChat
                            ? (isOtherOnline ? t('online') : t('offline'))
                            : t('participantsCount', { count: participantCount })}
                        {chatRoom.status === 'FINISHED' && ` • ${t('finished')}`}
                        {isMuted && ' • 🔇'}
                    </span>
                </div>

                <div className="chat-header-actions">
                    <button
                        className="chat-header-btn"
                        onClick={() => setShowParticipants(true)}
                        title={t('participants')}
                    >
                        <FiUsers />
                    </button>

                    <div className="chat-menu-wrapper">
                        <button
                            className="chat-header-btn"
                            onClick={() => setShowMenu(!showMenu)}
                        >
                            <FiMoreVertical />
                        </button>

                        {showMenu && (
                            <div className="chat-dropdown-menu">
                                <button onClick={handleToggleMute}>
                                    {isMuted ? <><FiBell /> {t('actions.unmute')}</> : <><FiBellOff /> {t('actions.mute')}</>}
                                </button>

                                {isTrainer && isAdmin && chatRoom.type === 'SCHOOL_ANNOUNCEMENT' && (
                                        <button onClick={handleAddAllOwners}>
                                            <FiUserPlus /> {t('actions.addAllOwners')}
                                        </button>
                                    )}

                                {isTrainer && isAdmin && chatRoom.type === 'TRAINING_GROUP' && chatRoom.status === 'ACTIVE' && (
                                    <button onClick={handleFinish}>
                                        <FiCheck /> {t('actions.finish')}
                                    </button>
                                )}

                                {isTrainer && isAdmin && chatRoom.type === 'TRAINING_GROUP' && chatRoom.status === 'FINISHED' && (
                                    <button onClick={handleRenew}>
                                        <FiRefreshCw /> {t('actions.renew')}
                                    </button>
                                )}

                                {isTrainer && isAdmin && (chatRoom.type === 'CUSTOM_GROUP' || chatRoom.type === 'TRAINER_CHAT') && (
                                    <button onClick={handleDelete} className="chat-menu-danger">
                                        <FiTrash2 /> {t('actions.delete')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showParticipants && (
                <ChatParticipantsPanel
                    chatRoom={chatRoom}
                    userId={userId}
                    isTrainer={isTrainer}
                    schoolId={schoolId}
                    onClose={() => setShowParticipants(false)}
                />
            )}
        </>
    );
};

export default ChatHeader;
